import { Table, TableColumnType } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { Response } from '../../api/util/with-response-formatter-interceptor'
import { UnauthorizedBom } from '../../models/models'
import { toastFailure } from '../../util/toast'
import useDebounce from '../project-master/single-project-view/hooks'
import moment from 'moment'

interface Props {
    pagination?: boolean
    //antd ColumnType<T> does not have title property
    columns?: Array<TableColumnType<UnauthorizedBom>>
    actionComponents?: Array<(UnauthorizedBom: UnauthorizedBom) => JSX.Element>
    dependencies?: any
    request: (pagination?: {
        skip: number
        limit: number
    }) => Promise<Response<{ count: number; entities: UnauthorizedBom[] }>>
}

export const UnauthorizedBomsTableColumns: {
    [key in string]: TableColumnType<UnauthorizedBom>
} = {
    Code: {
        title: 'Code',
        render: (unauthorizedBom: UnauthorizedBom) => {
            return (
                <div>
                    {unauthorizedBom?.product?.code || unauthorizedBom?.unauthorizedProduct?.code}
                </div>
            )
        },
    },
    Description: {
        title: 'Description',
        render: (unauthorizedBom: UnauthorizedBom) => {
            return (
                <div>
                    {unauthorizedBom?.product?.description ||
                        unauthorizedBom?.unauthorizedProduct?.description}
                </div>
            )
        },
    },
    Name: {
        title: 'Name',
        render: (unauthorizedBom: UnauthorizedBom) => {
            return (
                <div>
                    {unauthorizedBom?.product?.name || unauthorizedBom?.unauthorizedProduct?.name}
                </div>
            )
        },
    },
    RequestedBy: {
        title: 'Requested by',
        render: (bom: UnauthorizedBom) => {
            return <div>{bom?.requestedByUser?.email || 'Unknown'}</div>
        },
    },
    RequestedFor: {
        title: 'Requested for',
        render: (bom: UnauthorizedBom) => {
            return <div>{bom?.requestedForProject?.name || 'Unknown'}</div>
        },
    },
    RequestedAt: {
        title: 'Requested at',
        render: (bom: UnauthorizedBom) => {
            return <div>{moment(bom.timeCreatedAt).format('DD/MM/YYYY')}</div>
        },
    },
} as const

const DEFAULT_COLUMNS = [
    UnauthorizedBomsTableColumns.Code,
    UnauthorizedBomsTableColumns.Description,
    UnauthorizedBomsTableColumns.Name,
    UnauthorizedBomsTableColumns.RequestedBy,
    UnauthorizedBomsTableColumns.RequestedFor,
    UnauthorizedBomsTableColumns.RequestedAt,
]

export default function UnauthorizedBomsTable({
    request,
    columns,
    pagination,
    actionComponents,
    dependencies,
}: Props) {
    const [UnauthorizedBoms, setUnauthorizedBoms] = useState<UnauthorizedBom[]>([])
    const [UnauthorizedBomsAmount, setUnauthorizedBomsAmount] = useState<number>(0)
    const rowsPerPageOptions = [10, 20, 50, 100]
    type RowsPerPageOptions = typeof rowsPerPageOptions[number] // 10 | 20 | 50 | 100
    const [page, setPage] = useState<number>(1)
    const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageOptions>(10)

    const getUnauthorizedBomsDeps = useDebounce(JSON.stringify(dependencies), 200)

    const pageChange = (page: number, pageSize: number) => {
        setPage(page)
        setRowsPerPage(pageSize)
    }

    const tableColumns = useMemo(() => {
        let cols = columns ? Object.values(columns) : DEFAULT_COLUMNS
        if (actionComponents) {
            cols = [
                ...cols,
                {
                    title: 'Actions',
                    key: 'actions',
                    render: (UnauthorizedBom: UnauthorizedBom) => {
                        return (<>{actionComponents.map((a) => a(UnauthorizedBom))}</>) as any
                    },
                },
            ]
        }
        return cols
    }, [columns])

    const getUnauthorizedBoms = async (resetPage = false) => {
        const response = await request({
            skip: page * rowsPerPage - rowsPerPage,
            limit: rowsPerPage,
        })

        if (response.successful) {
            const { count, entities } = response.data
            resetPage && setPage(1)
            setUnauthorizedBomsAmount(count)
            setUnauthorizedBoms(entities)
        } else {
            toastFailure(response.message)
        }
    }

    // this happens when dependencies change outside the table, e.g filters given in with getUnauthorizedBomsDeps
    useEffect(() => {
        getUnauthorizedBoms(true)
    }, [getUnauthorizedBomsDeps, rowsPerPage])

    // this happens when dependencies change outside the table, e.g filters given in with getUnauthorizedBomsDeps
    useEffect(() => {
        getUnauthorizedBoms()
    }, [page])

    return (
        <Table
            rowKey="id"
            columns={tableColumns}
            dataSource={UnauthorizedBoms}
            expandable={undefined}
            size="small"
            pagination={
                pagination
                    ? {
                          defaultPageSize: 10,
                          current: page,
                          onChange: pageChange,
                          total: UnauthorizedBomsAmount,
                      }
                    : false
            }
        />
    )
}
